import { FC, useState, useEffect } from "react"
import { TaskSubmissionsProps } from "./ChallengeSubscription"
import { useChallenge } from "../../stores/challenge"
import Card from "../Card"
import Col from "../Col"
import Row from "../Row"
import { submissionTestingStatusMap } from "../../constants/submission"
import { formatDateHour } from "../../helpers/date"
import Modal from "../Modal"
import PageLoader from "../PageLoader"
import Tag from "../Tag"
import useError from "../../hooks/useError"
import {
  ListTeamsSubscriptionsResponseItem,
  ListUsersSubscriptionsResponseItem,
  RequestPaginator,
  submissionListdata,
  SubmissionsService
} from "../../sdk"
import Code from "../Code"
import Table from "../Table"
import { SubmissionsType } from "../../types/challenge-store"
import Message from "../Message"

const TaskSubmissions: FC<TaskSubmissionsProps> = ({
  task: {
    id,
    title,
    pdfUrl,
    difficulty,
    maxScore
  }
}) => {
  const submissions = useChallenge(({ submissions }) => submissions)
  const setSubmissions = useChallenge(({ setSubmissions }) => setSubmissions)
  const challenge = useChallenge(({ challenge }) => challenge)

  const currentOpenSubscription = useChallenge(({ currentOpenSubscription }) => currentOpenSubscription)
  const currentSubscriptionId = challenge?.individual
    ? (currentOpenSubscription as ListUsersSubscriptionsResponseItem)?.user.id
    : (currentOpenSubscription as ListTeamsSubscriptionsResponseItem)?.team.id
  const currentSubmissions = submissions && submissions[currentSubscriptionId || -1]

  const taskSubmissions = currentSubmissions?.find(({ taskId }) => taskId === id)?.submissions

  const [currentOpenSubmission, setCurrentOpenSubmission] = useState<number | null>(null)
  const [currentOpenSubmissionCode, setCurrentOpenSubmissionCode] = useState<string | null>(null)
  const [submissionCodeLoading, setSubmissionCodeLoading] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(!taskSubmissions)

  const { handleError } = useError()

  useEffect(() => {
    if (!submissions || Object.keys(submissions).length === 0 || !currentSubmissions?.find(({ taskId }) => taskId === id)) {
      void (async(): Promise<void> => {
        setLoading(true)
        try {
          const tempSubmissions = submissions || {} as SubmissionsType
          let temporarySubmissionsArray: submissionListdata[] = []
          let nextPaginator: RequestPaginator | null = { offset: 0, limit: 100 }

          do {
            try {
              const { data, nextPaginator: next } = challenge?.individual
                ? await SubmissionsService.listUserSubmissions({
                  handler: { taskId: id, userId: currentSubscriptionId },
                  paginator: nextPaginator
                })
                : await SubmissionsService.listTeamSubmissions({
                  handler: { taskId: id, teamId: currentSubscriptionId },
                  paginator: nextPaginator
                })
              temporarySubmissionsArray = temporarySubmissionsArray.concat(data)
              nextPaginator = next as RequestPaginator
            } catch (err) {
              handleError(err)
            }
          } while (nextPaginator)

          if (tempSubmissions![currentSubscriptionId] && !tempSubmissions![currentSubscriptionId].find(({ taskId }) => taskId === id)) {
            tempSubmissions![currentSubscriptionId].push({ taskId: id, submissions: temporarySubmissionsArray })
          } else {
            tempSubmissions![currentSubscriptionId] = [{ taskId: id, submissions: temporarySubmissionsArray }]
          }

          setSubmissions(tempSubmissions)
        } catch (err) {
          handleError(err)
        }
        setLoading(false)
      })()
    }
  }, [id])

  useEffect(() => {
    if (currentOpenSubmission) {
      void (async(): Promise<void> => {
        try {
          const { data: { code } } = await SubmissionsService.getSubmission({
            getCode: true,
            handler: { submissionId: currentOpenSubmission }
          })
          setCurrentOpenSubmissionCode(code)
        } catch (err) {
          handleError(err)
        }
        setSubmissionCodeLoading(false)
      })()
    }
  }, [currentOpenSubmission])

  return (
    <>
      {loading ?
        <PageLoader className="relative my-8" />
        :
        taskSubmissions && taskSubmissions!.length > 0 ?
          <div>
            <h2 className="challenge-user__subtitle mb-6 mt-8">{title}</h2>
            <div className="challenge-user__card-container">
              <Col lg={6}>
                <Card heading="Dettagli Task">
                  <ul>
                    <Row as="li" justify="between" className="card__row">
                      <div className="card__row-title">ID</div>
                      {id}
                    </Row>
                    <Row as="li" justify="between" className="card__row">
                      <div className="card__row-title">Punteggio massimo</div>
                      {maxScore}
                    </Row>

                    <Row as="li" justify="between" className="card__row">
                      <div className="card__row-title">Miglior Risultato</div>
                      {taskSubmissions!.filter(({ taskId, best }) => {
                        return taskId === id && best
                      })[0].score || 0}
                    </Row>

                    <Row as="li" justify="between" className="card__row">
                      <div className="card__row-title">Difficoltà</div>
                      {difficulty}
                    </Row>

                    <Row as="li" justify="between" className="card__row">
                      <div className="card__row-title">PDF</div>
                      <a href={pdfUrl} target="_blank" className="challenge-user__link">Visualizza</a>
                    </Row>
                  </ul>
                </Card>
              </Col>
            </div>

            <h2 className="text-2xl font-semibold my-8">Submissions</h2>

            <div>
              <Table
                className="users-list__table"
                headings={["ID", "Stato Testing",  "Punteggio", "Data", "Codice", ""]}
                fixedColumns={["ID", ""]}
                rows={taskSubmissions.map(({
                  id,
                  testingStatus,
                  score,
                  timestamp,
                  best
                }) => {
                  return {
                    id: id.toString(),
                    children: [
                      <span>{id}</span>,
                      <span>{submissionTestingStatusMap[testingStatus]}</span>,
                      <span>{score || "/"}</span>,
                      <span>{formatDateHour(timestamp)}</span>,
                      <span
                        onClick={(): void => {
                          setCurrentOpenSubmission(id)
                          setSubmissionCodeLoading(true)
                        }}
                        className="text-indigo-700 underline hover:cursor-pointer"
                      >
                        Visualizza
                      </span>,
                      best && <Tag theme="green">BEST</Tag>
                    ]
                  }
                })}
              />
            </div>
            {currentOpenSubmission &&
            <Modal
              className="submission-code-modal"
              close={(): void => setCurrentOpenSubmission(null)}
              title="Codice Submission"
            >
              {submissionCodeLoading ?
                <PageLoader className="relative my-8" />
                :
                currentOpenSubmissionCode !== null ?
                  <Code code={currentOpenSubmissionCode!} language="javascript" />
                  :
                  <p className="text-red-500 font-semibold">Errore</p>
              }
            </Modal>
            }
          </div>
          : <Message title="Nessuna submission trovata" icon="file-off" className="mt-8" />
      }
    </>
  )
}

export default TaskSubmissions
